/* eslint-disable newline-per-chained-call */
/* eslint-disable no-restricted-globals */
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Theme } from '@material-ui/core';
import { EntrepreneurFinance } from '../../types/types';
import Form from '../../components/Form';
import Button from '../../components/Button';
import { StyledText } from '../../components/StyledText';
import { entPublishedFinancialSummary } from '../../lib/validationSchemas';
import { dollarFormatInstruction, wholeDollarsOmitTrailingZeros, formikSubmitDisabled } from '../../lib/utils';
import DollarInput from '../../components/DollarInput';

const useStyles = makeStyles((theme: Theme) => ({
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 3),
    minHeight: 60
  }
}));

// Number used to strip trailing 0s
const initialValues = (finance: EntrepreneurFinance) => ({
  financeSoughtHigh: finance.financeSoughtHigh ? wholeDollarsOmitTrailingZeros(finance.financeSoughtHigh) : finance.financeSoughtHigh,
  financeSoughtLow: finance.financeSoughtLow ? wholeDollarsOmitTrailingZeros(finance.financeSoughtLow) : finance.financeSoughtLow,
  financeRaised: finance.financeRaised ? wholeDollarsOmitTrailingZeros(finance.financeRaised) : finance.financeRaised,
  currentRevenue: finance.currentRevenue ? wholeDollarsOmitTrailingZeros(finance.currentRevenue) : finance.currentRevenue
});

const unpublishedValidationSchema = Yup.object().shape({
  financeSoughtLow: Yup.number().nullable().min(10000, 'Minimum $10,000.'),
  financeSoughtHigh: Yup.number().nullable()
    .when('financeSoughtLow',
      { is: (financeSoughtLow) => (financeSoughtLow == null),
        then: Yup.number().nullable().moreThan(0) })
    .when('financeSoughtLow', {
      is: (financeSoughtLow) => (financeSoughtLow != null),
      then: Yup.number().nullable().min((Yup.ref('financeSoughtLow')), 'Must be greater than or equal to Investment Low.')
    }),
  financeRaised: Yup.number().nullable().min(0),
  currentRevenue: Yup.number().nullable()
});

const getValidationSchema = (isPublished: boolean) => {
  if (isPublished) {
    return entPublishedFinancialSummary;
  } else {
    return unpublishedValidationSchema;
  }
};
interface EntrepreneurFinanceProps {
  profile: EntrepreneurFinance
  open: boolean
  isPublished: boolean
  handleClose: () => void
  handleSubmit: (values: any) => void
}

const EntrepreneurFinanceEdit: React.FC<EntrepreneurFinanceProps> = ({
  open,
  handleClose,
  handleSubmit,
  profile,
  isPublished
}) => {
  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(profile),
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: handleSubmit
  });
  const styles = useStyles();

  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Edit Financial Summary</StyledText>
          <StyledText color='textSecondary' variant='subtitle2'>{`${dollarFormatInstruction}`}</StyledText>
        </DialogTitle>
        <DialogContent>
          <DollarInput
            id='financeSoughtLow'
            label='Finance Sought Low'
            placeholder=''
            maxWidth='200px'
            autoFocus
          />
          <br />
          <DollarInput
            id='financeSoughtHigh'
            label='Finance Sought High'
            placeholder=''
            maxWidth='200px'
          />
          <br />
          <DollarInput
            id='financeRaised'
            label='Finance Raised'
            placeholder=''
            maxWidth='200px'
          />
          <br />
          <DollarInput
            id='currentRevenue'
            label='Current Revenue'
            placeholder=''
            maxWidth='200px'
            allowNegative
          />
        </DialogContent>
        <DialogActions className={styles.actionBar}>
          <Button
            onClick={handleCancel}
            type='button'
            variant='text'
            size='large'
          >
            Cancel
          </Button>
          {(formik.isSubmitting) ? <CircularProgress style={{ width: 30, height: 30 }} />
            : (
              <Button
                type='submit'
                variant='contained'
                disableElevation
                color='primary'
                size='large'
                disabled={formikSubmitDisabled(formik)}
              >
                Update
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EntrepreneurFinanceEdit;
